import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthStore } from '../store';
import { CreditCard, Lock, Loader2, X, AlertCircle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (v) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(v || 0);

/**
 * IyzicoPaymentModal (CheckoutForm flow)
 * Props:
 *  - open, onClose
 *  - kind: 'order' | 'cari'
 *  - amount (number, required for cari; for order shown read-only)
 *  - orderId (required when kind='order')
 *  - title?, subtitle?
 *  - onSuccess()
 */
export default function IyzicoPaymentModal({ open, onClose, kind, amount, orderId, title, subtitle, onSuccess }) {
  const { token } = useAuthStore();
  const [config, setConfig] = useState({ configured: false, mode: 'sandbox' });
  const [submitting, setSubmitting] = useState(false);
  const [paymentPageUrl, setPaymentPageUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [editAmount, setEditAmount] = useState(kind === 'cari' ? (amount || 0) : 0);

  useEffect(() => {
    if (!open) return;
    setPaymentPageUrl(null);
    setErrorMsg('');
    setEditAmount(kind === 'cari' ? (amount || 0) : 0);
    axios.get(`${API_URL}/api/iyzico/config`).then(r => setConfig(r.data)).catch(() => {});
  }, [open, kind, amount]);

  const startPayment = async () => {
    setErrorMsg('');
    if (kind === 'cari' && (!editAmount || editAmount <= 0)) return setErrorMsg('Tutar gerekli');

    setSubmitting(true);
    try {
      const url = kind === 'order'
        ? `${API_URL}/api/iyzico/pay/order/${orderId}`
        : `${API_URL}/api/iyzico/pay/cari`;
      const body = kind === 'order' ? {} : { amount: editAmount };
      const res = await axios.post(url, body, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.status === 'success' && res.data.paymentPageUrl) {
        setPaymentPageUrl(res.data.paymentPageUrl);
      } else {
        setErrorMsg(res.data.error || res.data.errorCode || 'Ödeme başlatılamadı');
      }
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || 'Ödeme isteği başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-4 bg-black/60" onClick={onClose} data-testid="iyzico-modal">
      <div className="bg-white w-full h-full sm:h-auto sm:max-h-[92vh] sm:max-w-lg sm:rounded-2xl overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-[#eef0f7] rounded-lg"><CreditCard className="w-4 h-4 text-[#1a224f]" /></div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{title || (kind === 'order' ? 'Sipariş Ödemesi' : 'Cari Borç Ödemesi')}</h2>
              {subtitle && <p className="text-[11px] text-gray-500">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="iyzico-close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {paymentPageUrl ? (
          <div className="p-0">
            <iframe
              title="Iyzico Ödeme"
              src={paymentPageUrl}
              className="w-full border-0"
              style={{ height: 'calc(92vh - 70px)', minHeight: 600 }}
              data-testid="iyzico-checkout-iframe"
            />
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${config.mode === 'sandbox' ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
                {config.mode === 'sandbox' ? 'Test (Sandbox)' : 'Canlı (Production)'}
              </span>
              <Lock className="w-3.5 h-3.5 text-gray-400" />
            </div>

            {!config.configured && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <p className="text-xs text-red-800">Iyzico yapılandırması eksik. Yöneticinize bildirin.</p>
              </div>
            )}

            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Ödenecek Tutar</label>
              {kind === 'cari' ? (
                <input
                  type="number" step="0.01" min="0.01"
                  value={editAmount}
                  onChange={(e) => setEditAmount(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-base font-semibold focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#5b67a0]"
                  data-testid="iyzico-amount"
                />
              ) : (
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-base font-bold text-gray-900">
                  {formatCurrency(amount)}
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1.5">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-700" />
                <p className="text-sm font-semibold text-blue-900">Iyzico ile Güvenli Ödeme</p>
              </div>
              <p className="text-[11px] text-blue-800">Bir sonraki adımda kart bilgilerinizi Iyzico'nun PCI-DSS sertifikalı sayfasında gireceksiniz.</p>
              <ul className="text-[11px] text-blue-800 list-disc list-inside space-y-0.5">
                <li>Tüm kartlar destekleniyor (Visa, MasterCard, Troy, Amex)</li>
                <li>İsterseniz 2/3/6/9 taksit seçilebilir</li>
                <li>3D Secure ile koruma</li>
              </ul>
            </div>

            {errorMsg && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <p className="text-xs text-red-800 break-words">{errorMsg}</p>
              </div>
            )}

            <button onClick={startPayment} disabled={submitting || !config.configured}
              className="w-full py-3 bg-[#1a224f] text-white rounded-lg text-sm font-semibold hover:bg-[#12173a] disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="iyzico-submit">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Yönlendiriliyor...</> : <><Lock className="w-4 h-4" />Ödemeyi Başlat {kind === 'cari' && editAmount > 0 ? `· ${formatCurrency(editAmount)}` : ''}</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
